import { useEffect, useState } from "react";
import { Shirt } from "lucide-react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { cn } from "@/lib/utils";

interface ClientOrderArtworkThumbProps {
  /** Signed custom-preview URL preferred; falls back to the variant image URL. */
  src: string | null;
  /** Accessible description — typically the product name. */
  alt: string;
  className?: string;
}

/**
 * BLU-660 — line-item artwork thumbnail with a graceful fallback. Renders the
 * signed custom-preview (or variant image) when present, and a neutral
 * garment glyph when there is no preview or the image fails to load — it reads
 * "no preview yet", not "broken image" (the source may simply have no artwork
 * yet). Read-only: this is a preview only — no download/source action is
 * exposed (BLU-654 leak surface).
 *
 * When there IS an image, hovering (or focusing) the thumbnail pops a 200px
 * preview — the same affordance the inventory table and bulk stock editor use
 * via ProductImageWithHover, so a client inspecting an order can confirm the
 * artwork without leaving the sheet. The fallback glyph stays inert: there is
 * nothing to enlarge.
 */
export function ClientOrderArtworkThumb({
  src,
  alt,
  className,
}: ClientOrderArtworkThumbProps) {
  const [errored, setErrored] = useState(false);

  // Reset the error latch when the source changes (e.g. sheet reused for a new
  // order) so a previously-broken URL doesn't suppress a good one.
  useEffect(() => {
    setErrored(false);
  }, [src]);

  const showImage = !!src && !errored;

  const frame = (
    <div
      className={cn(
        "flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/40",
        showImage &&
          "cursor-pointer transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
        className,
      )}
      // Focusable only when there is something to enlarge; Radix opens the
      // hover card on focus as well as hover. Deliberately NOT a <button> —
      // this thumbnail sits inside a row that may itself be clickable.
      {...(showImage
        ? { tabIndex: 0, role: "img" as const, "aria-label": alt }
        : {})}
    >
      {showImage ? (
        <img
          src={src}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="h-full w-full object-cover"
          onError={() => setErrored(true)}
        />
      ) : (
        <Shirt className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
      )}
    </div>
  );

  if (!showImage) return frame;

  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>{frame}</HoverCardTrigger>
      <HoverCardContent className="w-auto p-2" side="right" align="center">
        <div className="h-[200px] w-[200px] overflow-hidden rounded">
          <img
            src={src}
            alt={alt}
            className="h-full w-full bg-white object-contain"
            onError={() => setErrored(true)}
          />
        </div>
        {/* Clamped to the image width — product names run long and would
            otherwise stretch the w-auto card across the pane. */}
        <p className="mt-2 max-w-[200px] text-center text-xs text-muted-foreground">
          {alt}
        </p>
      </HoverCardContent>
    </HoverCard>
  );
}
